import React, { FC, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
    getKpiTableColumns,
    getRecommendationCalenderColumns,
    getRecommendationEventTableColumns,
} from 'utils/getRecommendationTableColumns';
import { calculateContainerHeight, getConfig, parseSliderValue } from 'utils/helperMethods';
import { CustomSlider, sliderWidth } from '../BumpReport/MainSection';
import styled from '@emotion/styled';
import { Spin, Table } from 'antd';
import { colorConstants } from 'config/colorConstants';
import tw from 'twin.macro';
import Legend from 'components/molecules/Legend';
import { promoRecommendation } from 'contexts/PromoRecommendationContext';
import { UserContext } from 'contexts/ConfigContext';
import ConfirmEventModal from 'components/organism/PromoRecommendation/ConfirmationModal';
import { useDeleteEvent } from 'hooks/promoRecommendation/useDeleteEvent';
import { newPlanValue } from 'hooks/promoRecommendation/useFetchGetPlans';
import { trackEventMixpanel } from 'utils/mixpanel';
import { PROMO_RECOMMENDATION_BAR_SLIDER_CHANGED } from 'utils/mixpanelEventNames';
import InputModal from 'components/molecules/common/modals/InputModal';
import { antIcon } from 'components/pages/Login';
import useUpdateEventName from 'hooks/promoRecommendation/useUpdateEventName';
import { FilterContextData } from 'contexts/FilterContext';
import { sliderMarksConfig } from 'config/constants';

const CustomTable = styled(Table)`
    .ant-spin-nested-loading > div > .ant-spin {
        max-height: initial;
    }
    .ant-table-thead > tr > th {
        background: ${colorConstants.grey};
        padding: 0px;
        min-height: 44px;
    }
    .ant-table-body {
        overflow-y: auto !important;
    }
    .ant-table-cell-fix-left,
    .ant-table-cell-fix-right {
        background: unset;
    }
    // .ant-table-cell-scrollbar {
    //     display: none !important;
    // }
    .bold {
        font-weight: bold;
    }
    .ant-table-header col:not(:last-child),
    .ant-table-body col {
        min-width: 50px;
    }
    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }
    .ant-table-column-sorter {
        position: relative;
        left: -10px;
    }
    .ant-table-column-sorter-down.active,
    .ant-table-column-sorter-up.active {
        ${tw`text-purpleFont`};
    }
    ${tw`border-t`}
    border-bottom: unset;
    ${tw`border border-borderLinePurpuleAlt`}
`;

interface Props {
    activeType: any;
}

const tableHeight = calculateContainerHeight(1, 48 + 36 + 16 + 32 + 16 + 42 + 74 + 37);
const eventTableWrapperId = 'promo-recommendation-events-table-wrapper';
const calenderWrapperId = 'recommendation-calender-wrapper';

const calenderKey = 'Calender';

const emptyText = 'Click on Add Event to start adding events.';
export type TableProductType = {
    Account: null | 'all' | number;
    Products: null | 'all' | number;
};

const commonClassName = 'w-full h-[40px] border-t-[0px] rounded-b border border-borderLinePurpuleAlt';

const disabledClassName = '';

const RecommendationMainSection: FC<Props> = ({ activeType }) => {
    const { appConfig } = useContext(UserContext);
    const { currentFrequency } = useContext(FilterContextData);

    const sliderConfig = sliderMarksConfig?.[currentFrequency?.key ?? 'W'];
    const sliderConfigKeys = Object.keys(sliderConfig);
    const sliderMin = sliderConfigKeys?.[0];
    const sliderMax = sliderConfigKeys?.[sliderConfigKeys?.length - 1];

    const [expandedProducts, setExpandedProducts] = useState<TableProductType>({
        Account: null,
        Products: null,
    });
    const [eventsTableRowHeight, setEventsTableRowHeight] = useState<any[]>([]);
    const [sliderValue, setSliderValue] = useState(parseSliderValue(sliderConfigKeys?.[1]));
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [showInputNameModal, setShowInputNameModal] = useState<boolean>(false);
    const [editNameInfo, setEditNameInfo] = useState<{
        index: number | null;
        eventId: number | null;
        eventName: string | null;
    }>({ index: null, eventId: null, eventName: null });

    const promoRecommendationConfig = getConfig('promoRecommendation', appConfig);
    const enableEventNameEdit = promoRecommendationConfig?.enableEventNameEdit ?? false;
    const promoTypeColorMapping = promoRecommendationConfig.promoTypeColorMapping;
    const calenderRef = useRef<any>(null);

    const {
        selectedPlan,
        selectedKpiList,
        eventsListData,
        eventsListError,
        eventsListLoading,
        deleteEvent,
        fetchEventsList,
        clearEventsList,
        editPlan,
        loadingForPlanList,
    } = useContext(promoRecommendation);

    //stores the delete index
    const toDeleteId = useRef<any>({ index: null, id: null });
    //hooks

    //delete event hook
    const {
        loading: deleteEventLoading,
        error: deleteEventError,
        deletePlanEvent,
    } = useDeleteEvent({ onComplete: onDeleteQueryComplete });

    const onCompleteUpdateEventName = () => {
        setShowInputNameModal(false);
        setEditNameInfo({ index: null, eventId: null, eventName: null });
        fetchEventsList({
            variables: {
                planId: selectedPlan?.planId,
                frequency: currentFrequency?.key,
            },
        });
    };
    const { loading: updateEventNameLoading, updateEventNameFunction } = useUpdateEventName(onCompleteUpdateEventName);

    //called when clicked on delete icon
    function onDeleteHandler(index: number, id: number, parentId: number | null) {
        toDeleteId.current = { index, id, parentId };
        setShowConfirmationModal(true);
    }

    //called when deleteEvent query is completed
    function onDeleteQueryComplete() {
        fetchEventsList({
            variables: {
                planId: selectedPlan?.planId,
                frequency: currentFrequency?.key,
            },
        });
        //deleteEvent(Number(toDeleteId.current.index));
    }

    //called when clicked on yes button in modal
    function deleteEventHandler() {
        if (!isNaN(Number(toDeleteId.current.id))) {
            const tempObj = {
                event_id: Number(toDeleteId.current.id),
                parent_id: toDeleteId.current?.parentId,
            };
            deletePlanEvent({
                variables: {
                    operationType: 'delete',
                    planId: selectedPlan?.planId,
                    eventsData: [JSON.stringify(tempObj)],
                    frequency: currentFrequency?.key,
                },
            });
            setShowConfirmationModal(false);
        }
    }

    const calenderWidth = calenderRef.current?.offsetWidth || 0;

    //events list table columns
    const eventListColumns = useMemo(() => {
        if (eventsListData?.eventsData)
            return getRecommendationEventTableColumns(
                eventsListData?.eventsData,
                expandedProducts,
                setExpandedProducts,
                null,
                onDeleteHandler,
                editPlan,
                setShowInputNameModal,
                setEditNameInfo,
                enableEventNameEdit,
                selectedPlan?.saveType,
            );
        return undefined;
    }, [expandedProducts, eventsListData, selectedPlan?.saveType]);

    //legend data for calender table
    const legendData = useMemo(() => {
        const legendData = Object.keys(promoTypeColorMapping).map((item) => {
            const { label, color } = promoTypeColorMapping[item];
            return {
                label,
                color,
            };
        });
        return legendData;
    }, []);

    //calender/kpi table columns
    const calenderKpiColumns = useMemo(() => {
        const leftOutHeight = Math.max(
            0,
            tableHeight - (eventsTableRowHeight.reduce((prev, current) => prev + current, 0) + 10 + 40),
        );
        if (eventsListData?.calenderData) {
            if (activeType?.key === calenderKey) {
                return getRecommendationCalenderColumns(
                    eventsListData?.calenderData,
                    (calenderWidth / sliderValue) * 4, //adding 2 as buffer
                    eventsTableRowHeight,
                    leftOutHeight,
                    null,
                    eventsListData?.tooltipMapping || [],
                );
            } else {
                return getKpiTableColumns(
                    eventsListData?.kpiData,
                    eventsTableRowHeight,
                    null,
                    selectedKpiList,
                    eventsListData?.metricMeta || {},
                );
            }
        }
        return undefined;
    }, [eventsTableRowHeight, eventsListData, sliderValue, activeType, selectedKpiList]);

    //slider on change handler
    const handleSliderChange = (value: number) => {
        const sliderValue = value === 32 ? 26 : value;
        setSliderValue(sliderValue);
        trackEventMixpanel(PROMO_RECOMMENDATION_BAR_SLIDER_CHANGED, {
            sliderValue,
        });
    };

    //fetching events for a particular plan
    useEffect(() => {
        if (selectedPlan && 'planId' in selectedPlan && 'value' in selectedPlan) {
            //if user has selected new plan then reset the events list
            if (selectedPlan['value'] === newPlanValue) {
                clearEventsList();
                return;
            }
            fetchEventsList({
                variables: {
                    planId: selectedPlan?.planId,
                    frequency: currentFrequency?.key,
                },
            });
        } else if (eventsListData) {
            clearEventsList();
        }
    }, [selectedPlan]);

    useEffect(() => {
        setSliderValue(parseSliderValue(sliderConfigKeys?.[1]));
    }, [currentFrequency]);

    //calculate the height of each row from event list table,
    // so that the same can be used in calender table to maintain same height for each row
    useLayoutEffect(() => {
        if (eventsListData) {
            let divElements: any = document
                .getElementById(eventTableWrapperId)
                ?.getElementsByClassName('ant-table-row-level-0');
            divElements = divElements ? [...divElements] : [];
            if (Array.isArray(divElements)) {
                const tempHeight = divElements.map((item) => item?.clientHeight);
                setEventsTableRowHeight(tempHeight);
            }
        }
    }, [expandedProducts, eventsListData]);

    //scroll sync logic for events table and calender table
    useLayoutEffect(() => {
        const eventsListTableId = document
            .getElementById(eventTableWrapperId)
            ?.getElementsByClassName('ant-table-body')[0];
        const calenderTableId = document.getElementById(calenderWrapperId)?.getElementsByClassName('ant-table-body')[0];
        let isCalenderScrolling = false;
        //scroll table when user is scrolling bump chart
        function scrollFunctionEventsListTable(event: any) {
            if (!isCalenderScrolling) return;
            //if scrolling coming from events table, then dont trigger calender table event listener
            //this is to avoid multiple event listeners from calling

            eventsListTableId?.scrollTo({
                top: Number(calenderTableId?.scrollTop) * 1, //giving a speed because widths of table and chart are different
                behavior: 'auto',
            });
        }
        function scrollFunctionCalenderTable(event: any) {
            if (isCalenderScrolling) return;
            //if scrolling coming from calender, then dont trigger events list table scroll event listener
            //this is to avoid multiple event listeners from calling

            calenderTableId?.scrollTo({
                top: Number(eventsListTableId?.scrollTop) * 1, //giving a speed because widths of table and chart are different
                behavior: 'auto',
            });
        }
        //doing this to avoid scroll event being called when scrolling events list table
        function mouseEnterEventFunction() {
            isCalenderScrolling = true;
            calenderTableId?.addEventListener('scroll', scrollFunctionEventsListTable, { passive: true });
        }
        function mouseLeaveEventFunction() {
            isCalenderScrolling = false;
            calenderTableId?.removeEventListener('scroll', scrollFunctionEventsListTable);
        }
        calenderTableId?.addEventListener('mouseenter', mouseEnterEventFunction);
        calenderTableId?.addEventListener('mouseleave', mouseLeaveEventFunction);
        eventsListTableId?.addEventListener('scroll', scrollFunctionCalenderTable, { passive: true });

        return () => {
            eventsListTableId?.removeEventListener('scroll', scrollFunctionCalenderTable);
            calenderTableId?.removeEventListener('mouseenter', mouseEnterEventFunction);
            calenderTableId?.removeEventListener('mouseleave', mouseLeaveEventFunction);
        };
    }, []);

    //finding the calender header height and assigning the same to events list height so that header height remains the same
    useLayoutEffect(() => {
        //adding a set timeout to set the delay
        setTimeout(() => {
            const eventsListTableId = document
                .getElementById(eventTableWrapperId)
                ?.getElementsByClassName('ant-table-thead')[0] as HTMLElement;
            const calenderTableId = document
                .getElementById(calenderWrapperId)
                ?.getElementsByClassName('ant-table-thead')[0];
            if (eventsListTableId && 'style' in eventsListTableId && calenderTableId) {
                eventsListTableId.style.height = `${calenderTableId?.clientHeight}px`;
            }
        }, 10);
    }, [calenderKpiColumns]);

    const commonLoading = eventsListLoading || deleteEventLoading || loadingForPlanList;
    return (
        <div>
            <section className="flex gap-4 mt-4">
                <div id={eventTableWrapperId} className="w-[45%]">
                    {/* EVENTS TABLE */}
                    <CustomTable
                        loading={commonLoading}
                        style={{ minHeight: tableHeight }}
                        //bordered
                        pagination={false}
                        scroll={{ x: '100%', y: tableHeight }}
                        sticky
                        rowClassName={(record: any, index) => {
                            return record?.isActive === 'disabled' ? disabledClassName : '';
                        }}
                        // rowKey={(record: any) => record[rowKey]}
                        columns={eventListColumns}
                        dataSource={eventsListData?.eventsData}
                        locale={{
                            emptyText: (
                                <div
                                    className="flex font-md text-borderGrey  justify-center items-center"
                                    style={{ height: tableHeight - (16 * 2 + 1) }}
                                >
                                    {emptyText}
                                </div>
                            ),
                        }}
                        // loading={tableLoading}
                    />
                    <div className={commonClassName}> </div>
                </div>
                <div className="w-[55%]">
                    <div ref={calenderRef} id={calenderWrapperId} className="mr-4">
                        {/* CALENDER TABLE */}
                        {activeType?.key === calenderKey ? (
                            <CustomTable
                                loading={commonLoading}
                                style={{ minHeight: tableHeight }}
                                //bordered
                                pagination={false}
                                scroll={{ x: '100%', y: tableHeight }}
                                sticky
                                columns={calenderKpiColumns}
                                dataSource={eventsListData?.calenderData}
                                // loading={tableLoading}
                                rowClassName={(record: any, index) => {
                                    return record?.isActive === 'disabled' ? disabledClassName : '';
                                }}
                                locale={{
                                    emptyText: (
                                        <div
                                            className="flex font-md text-borderGrey justify-center items-center"
                                            style={{ height: tableHeight - (16 * 2 + 1) }}
                                        >
                                            {emptyText}
                                        </div>
                                    ),
                                }}
                            />
                        ) : (
                            // KPI TABLE
                            <CustomTable
                                style={{ minHeight: tableHeight }}
                                //bordered
                                loading={commonLoading}
                                pagination={false}
                                scroll={{ x: '100%', y: tableHeight }}
                                sticky
                                rowClassName={(record: any, index) => {
                                    return record?.isActive === 'disabled' ? disabledClassName : '';
                                }}
                                // rowKey={(record: any) => record[rowKey]}
                                columns={calenderKpiColumns}
                                dataSource={eventsListData?.kpiData}
                                // loading={tableLoading}
                                locale={{
                                    emptyText: (
                                        <div
                                            className="flex font-md text-borderGrey justify-center items-center"
                                            style={{ height: tableHeight - (16 * 2 + 1) }}
                                        >
                                            {emptyText}
                                        </div>
                                    ),
                                }}
                            />
                        )}

                        <div className={commonClassName}>
                            {activeType.key === calenderKey && eventsListData?.calenderData?.length > 0 ? (
                                <div className="flex items-center h-full">
                                    {/* safe center keeps the first item reachable when the legend overflows */}
                                    <Legend
                                        data={legendData}
                                        containerClassName="w-full"
                                        style={{ justifyContent: 'safe center' }}
                                    />
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </section>
            {activeType.key === calenderKey && (
                <div className="flex justify-end">
                    <CustomSlider
                        style={{ width: sliderWidth }}
                        max={Number(sliderMax)}
                        min={Number(sliderMin)}
                        step={null}
                        marks={sliderConfig}
                        value={sliderValue}
                        tooltip={{ open: false }}
                        onChange={handleSliderChange}
                    />
                </div>
            )}
            <ConfirmEventModal
                onYes={deleteEventHandler}
                setShowModal={setShowConfirmationModal}
                showModal={showConfirmationModal}
            />
            <InputModal
                open={showInputNameModal}
                onCancel={() => {
                    setShowInputNameModal(false);
                }}
                onSubmitClick={(value) => {
                    updateEventNameFunction({
                        variables: {
                            eventId: editNameInfo.eventId,
                            updatedName: value,
                        },
                    });
                }}
                okText={updateEventNameLoading ? <Spin indicator={antIcon} /> : 'Save'}
                okButtonProps={{ style: { borderRadius: 6 } }}
                title="Edit Event Name"
                inputValue={editNameInfo.eventName ?? ''}
                onInputChange={(e) => {
                    setEditNameInfo((prev) => ({ ...prev, eventName: e.target.value }));
                }}
            />
        </div>
    );
};

export default React.memo(RecommendationMainSection);
